import { DND_KEYBOARD_INSTRUCTIONS_ID, DND_LIVE_REGION_ID } from './attributes'

// Screen-reader output for the keyboard path. The live region is created on the
// first keyboard lift, so pointer-only pages never pay for it. The instructions
// node is created when a kit registers, because draggables reference it before
// any lift; the lift recreates it if the page removed it since.

let liveRegion: HTMLElement | null = null

function hideFromSightedUsers(element: HTMLElement): void {
    // Visually-hidden but still exposed to assistive technology.
    // top and left keep the node inside the viewport so it does not extend document scroll height.
    element.style.position = 'absolute'
    element.style.top = '0'
    element.style.left = '0'
    element.style.width = '1px'
    element.style.height = '1px'
    element.style.overflow = 'hidden'
    element.style.clipPath = 'inset(50%)'
    element.style.whiteSpace = 'nowrap'
}

// Static instructions referenced by the bindings' aria-describedby. Created
// here so the bindings stay pure prop builders with zero effects. Screen
// readers read the description when a draggable is reached, also in browse
// mode where no focus event fires, so the node must exist before any lift.
export function ensureKeyboardInstructions(): void {
    // Kits register at module scope, which may run during SSR or before <body> is parsed.
    if (typeof document === 'undefined' || document.body === null) return
    if (document.getElementById(DND_KEYBOARD_INSTRUCTIONS_ID) !== null) return
    const instructions = document.createElement('div')
    instructions.id = DND_KEYBOARD_INSTRUCTIONS_ID
    instructions.textContent = 'Press Space to lift, arrow keys to move between drop targets, Space to drop, Escape to cancel.'
    hideFromSightedUsers(instructions)
    document.body.appendChild(instructions)
}

function ensureLiveRegion(): HTMLElement {
    if (liveRegion !== null && liveRegion.isConnected) return liveRegion
    const existing = document.getElementById(DND_LIVE_REGION_ID)
    if (existing instanceof HTMLElement) {
        liveRegion = existing
        return liveRegion
    }
    const region = document.createElement('div')
    region.id = DND_LIVE_REGION_ID
    region.setAttribute('role', 'status')
    region.setAttribute('aria-live', 'polite')
    region.setAttribute('aria-atomic', 'true')
    hideFromSightedUsers(region)
    document.body.appendChild(region)
    liveRegion = region
    ensureKeyboardInstructions()
    return liveRegion
}

// The accessibility tree only sees the text at the end of a task, so a clear
// and a set in the same task look like no change when the message repeats
// (for example, pressing Space twice on a rejected target). Setting the text in
// a later task makes repeated messages announce again.
const ANNOUNCE_DELAY_MS = 50
let pendingAnnouncement: number | null = null

export function cancelPendingAnnouncement(): void {
    if (pendingAnnouncement === null) return
    window.clearTimeout(pendingAnnouncement)
    pendingAnnouncement = null
}

export function announce(message: string): void {
    const region = ensureLiveRegion()
    region.textContent = ''
    cancelPendingAnnouncement()
    pendingAnnouncement = window.setTimeout(() => {
        pendingAnnouncement = null
        region.textContent = message
    }, ANNOUNCE_DELAY_MS)
}
